import { Component } from '@angular/core';
import { CurrenciesService } from '@commons/services/currencies/currencies.service';
import { NotificationService } from '@commons/notifications/services/notification.service';
import { NOTIFICATIONS_GET_ENDPOINT } from '@commons/constants';
import { CognitoAuthService } from '@commons/services/cognito-auth/cognito-auth.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

declare var $: any;

import { MessagingService } from './messaging/messaging.service';
import { LocationService } from './authentication/services/location.service';
import { AboutService } from '@commons/services/about/about.service';
import { TranslateModule } from '@ngx-translate/core';
import { Toast } from 'primeng/toast';
import { MatIconModule } from '@angular/material/icon';

import { AudioService } from '@commons/services/audio/audio.service';
import { PrimeTemplate } from 'primeng/api';

/**
 * Root component of the Sequoia Web application.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [Toast, MatIconModule, TranslateModule, RouterModule, PrimeTemplate],
  styleUrls: ['./app.component.scss'],
  standalone: true,
})
export class AppComponent {
  title = 'sequoia-web';
  getNotificationsEndpoint: string = NOTIFICATIONS_GET_ENDPOINT;

  /**
   * /**.
   */
  /**
   * Creates an instance of AppComponent.
   *
   * @param currenciesService - Service to manage currency data.
   * @param notificationService - Service to manage user notifications.
   * @param messagingService - Firebase Cloud Messaging integration service.
   * @param router - Angular router for navigation events.
   * @param locationService - Service to load warehouse locations.
   * @param aboutService - Service to load application metadata.
   * @param audioService - Service to manage audio feedback.
   * @param authService - Cognito authentication service.
   */
  constructor(
    private currenciesService: CurrenciesService,
    private notificationService: NotificationService,
    private messagingService: MessagingService,
    private router: Router,
    private readonly locationService: LocationService,
    private readonly aboutService: AboutService,
    readonly audioService: AudioService,
    private readonly authService: CognitoAuthService,
  ) {
    this.currenciesService.getCurrencies();
    this.loadLocations();
    this.aboutService.getDataAbout();
    this.getNotifications();
  }

  /**
   * Fetches the user notifications from the notification service.
   *
   * @returns {Promise<void>} A promise that resolves when notifications have been fetched.
   */
  async getNotifications(): Promise<void> {
    try {
      const { tokens } = await this.authService.currentSession();
      this.notificationService
        .getNotifications(this.getNotificationsEndpoint)
        .subscribe();
    } catch (err) {
      return;
    }
  }

  /**
   * Loads locations after authentication.
   *
   * @returns {Promise<void>} A promise that resolves when locations are requested.
   */
  private async loadLocations(): Promise<void> {
    try {
      const { tokens } = await this.authService.currentSession();
      this.locationService.getLocations();
    } catch (error) {
      return;
    }
  }

  /**
   * Initializes the component lifecycle.
   *
   * @returns {void}
   */
  ngOnInit(): void {
    this.modalGeneralHide();
    this.notificationConfigurate();
    this.messagingService._onMessage();
    if (Capacitor.isNativePlatform()) {
      this.registerBackButton();
    }
  }

  /**
   * Configures service worker message listeners for notification clicks.
   *
   * @returns {void}
   */
  notificationConfigurate(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.messageType == 'notification-clicked') {
          this.getNotifications();
        }
      });
    }
  }

  /**
   * Registers the Capacitor hardware back-button listener for Android.
   * From root screens (home, login, splash) the app is minimized instead of navigating back.
   * From any other screen the user is taken to home.
   *
   * @returns {void}
   */
  private registerBackButton(): void {
    App.addListener('backButton', () => {
      const currentUrl = this.router.url;
      const isRoot =
        currentUrl === '/home' || currentUrl === '/login' || currentUrl === '/';
      if (isRoot) {
        App.minimizeApp();
      } else {
        this.router.navigate(['/home'], { replaceUrl: true });
      }
    });
  }

  /**
   * Hides active Bootstrap modals when Angular navigation finishes.
   *
   * @returns {void}
   */
  modalGeneralHide(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((_event) => {
        $('#modal_general').modal('hide');
        $('#modal_create_inbound').modal('hide');
        $('#modal_upload_file').modal('hide');
        $('#modal_print_bar_code').modal('hide');
      });
  }

  /**
   * Opens the notifications sidebar through the notification service.
   *
   * @returns {void}
   */
  openNotifications(): void {
    this.notificationService.openNotifications();
  }
}
